import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  FlatList,
  Image,
  ActivityIndicator,
  Alert,
  Modal,
  ScrollView,
  Animated,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import colors from '../theme/colors';
import { useAuth } from '../context/AuthContext';
import { useNavigationVisibility } from '../context/NavigationVisibilityContext';
import api from '../services/api';

const ProfilesScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { user, logout } = useAuth();
  const { handleScroll, headerAnimation } = useNavigationVisibility();
  const [activeTab, setActiveTab] = useState('brides');
  const [searchQuery, setSearchQuery] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showFilterSortModal, setShowFilterSortModal] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  useEffect(() => {
    loadProfiles();
  }, [activeTab]);

  // Listen for shouldRefresh parameter from MemberProfileScreen
  useEffect(() => {
    if (route.params?.shouldRefresh) {
      loadProfiles();
      // Clear the shouldRefresh param
      navigation.setParams({ shouldRefresh: false });
    }
  }, [route.params?.shouldRefresh]);

  // Reload profiles when returning to this screen (e.g., after editing or adding a profile)
  useFocusEffect(
    React.useCallback(() => {
      // Reload profiles when screen comes into focus
      // Skip only on the very first mount (when isLoading is true from initial load)
      if (!isLoading) {
        loadProfiles();
      }
    }, [activeTab])
  );

  const loadProfiles = async () => {
    console.log('loadProfiles called, starting fetch...');
    setIsLoading(true);
    try {
      // Determine member type based on active tab
      const memberType = activeTab === 'brides' ? 'Bride' : 'Groom';
      
      console.log('User context:', user);
      console.log('MediatorId:', user?.mediatorId);
      
      // API request payload - only include fields with values
      const requestData = {
        mediatorId: user?.mediatorId || 1, // Use mediatorId from user context
        memberType: memberType,
        pageSize: 20,
        pageNumber: currentPage,
      };

      // Only add searchTerm if it has a value
      if (searchQuery && searchQuery.trim()) {
        requestData.searchTerm = searchQuery.trim();
      }

      console.log('API Request:', requestData);

      const response = await api.post('/api/EnhancedMember/list', requestData);
      
      console.log('API Response:', response.data);
      console.log('Members count:', response.data?.members?.length);
      
      if (response.data && response.data.members) {
        // Default icons for missing photos
        const BRIDE_ICON = 'https://kalyanamitra.in/assets/ps-bride-UWGtbgl1.png';
        const GROOM_ICON = 'https://kalyanamitra.in/assets/ps-groom-D-L2Y5H0.png';
        
        // Map API response to profile structure
        const mappedProfiles = response.data.members.map(member => {
          const defaultIcon = memberType === 'Bride' ? BRIDE_ICON : GROOM_ICON;
          return {
            id: member.memberId,
            name: `${member.name} ${member.surName}`,
            age: member.age,
            location: member.city || member.state || 'Location not specified',
            profession: member.profession || 'Not specified',
            education: member.highestEducationQualification || 'Not specified',
            image: member.photos && member.photos.length > 0 
              ? member.photos[0].securedUrl 
              : defaultIcon,
            verified: member.isApproved,
            approvalStatus: member.isApproved === true ? 'approved' : 'pending',
            premium: false, // Adjust based on actual premium field if available
            completionPercentage: member.profileCompletionPercentage || 0,
          };
        });
        
        console.log('Mapped profiles:', mappedProfiles);
        setProfiles(mappedProfiles);
        setTotalRecords(response.data.totalRecords || 0);
      } else {
        console.log('No members in response');
        setProfiles([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error stack:', error.stack);
      Alert.alert('Error', `Failed to load profiles: ${error.response?.data?.message || error.message}`);
      setProfiles([]);
    } finally {
      console.log('loadProfiles finished, setting isLoading to false');
      setIsLoading(false);
    }
  };

  const handleSearchChange = (text) => {
    setSearchQuery(text);
    
    // Clear existing timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    // Set new timer for debounced search
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadProfiles();
    }, 500); // 500ms debounce
    
    setSearchDebounceTimer(timer);
  };

  const handleCloseSearch = () => {
    setShowSearchBar(false);
    setSearchQuery('');
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    setCurrentPage(1);
    loadProfiles();
  };

  const handleLogout = () => {
    Alert.alert(
      'Logout',
      'Are you sure you want to logout?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Logout',
          onPress: async () => {
            await logout();
            navigation.replace('Home');
          },
        },
      ]
    );
  };

  const renderProfileCard = ({ item }) => {
    return (
    <View style={styles.profileCardWrapper}>
      <TouchableOpacity 
        style={styles.profileCard}
        onPress={() => {
          console.log('Card clicked, memberId:', item.id);
          console.log('Navigation object:', navigation);
          navigation.push('MemberProfile', { 
            memberId: item.id,
            returnScreen: 'Profiles'
          });
        }}
        activeOpacity={0.7}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.profileName}>{item.name}</Text>
          <View style={styles.actionButtonsContainer}>
            <TouchableOpacity 
              style={styles.iconActionButton}
              onPress={(e) => {
                e.stopPropagation();
                navigation.push('ShareProfile', { memberId: item.id });
              }}
            >
              <SvgIcon name="share-outline" size={20} color={colors.secondary} />
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.iconActionButton}
              onPress={(e) => {
                e.stopPropagation();
                navigation.push('ProfileDetail', { 
                  memberId: item.id,
                  returnScreen: 'Profiles'
                });
              }}
            >
              <SvgIcon name="eye-outline" size={20} color={colors.secondary} />
            </TouchableOpacity>
          </View>
        </View>

      <View style={styles.cardBody}>
        <View style={styles.imageContainer}>
          <Image source={{ uri: item.image }} style={styles.profileImage} />
          
          {/* Approval Status Badge */}
          {item.approvalStatus === 'approved' && (
            <View style={styles.approvedBadge}>
              <SvgIcon name="checkmark" size={20} color="#00D9B5" />
            </View>
          )}
          {item.approvalStatus === 'pending' && (
            <View style={styles.pendingBadge}>
              <SvgIcon name="hourglass-outline" size={18} color="#FFA500" />
            </View>
          )}
          
          {item.premium && (
            <View style={styles.premiumBadge}>
              <Text style={styles.premiumText}>PRO</Text>
            </View>
          )}
          {item.completionPercentage !== undefined && (
            <View style={[
              styles.completionBadge,
              { 
                backgroundColor: colors.primary,
                borderColor: item.completionPercentage >= 80 ? '#00D9B5' : item.completionPercentage >= 50 ? '#FFA500' : '#FF6B6B'
              }
            ]}>
              <Text style={[
                styles.completionBadgeText,
                { color: item.completionPercentage >= 80 ? '#00D9B5' : item.completionPercentage >= 50 ? '#FFA500' : '#FF6B6B' }
              ]}>{item.completionPercentage}%</Text>
            </View>
          )}
        </View>

        <View style={styles.profileInfo}>
          <View style={styles.infoRow}>
            <SvgIcon name="calendar-outline" size={18} color={colors.accent} />
            <Text style={styles.infoText}>{item.age} years</Text>
          </View>
          <View style={styles.infoRow}>
            <SvgIcon name="location-outline" size={18} color={colors.accent} />
            <Text style={styles.infoText}>{item.location}</Text>
          </View>
          <View style={styles.infoRow}>
            <SvgIcon name="briefcase-outline" size={18} color={colors.accent} />
            <Text style={styles.infoText}>{item.profession}</Text>
          </View>
          <View style={styles.infoRow}>
            <SvgIcon name="school-outline" size={18} color={colors.accent} />
            <Text style={styles.infoText}>{item.education}</Text>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Compact Header with Tabs on Left and Actions on Right */}
      <Animated.View style={[styles.compactHeader, { transform: [{ translateY: headerAnimation }] }]}>
        {/* Tabs on Left */}
        <View style={styles.inlineTabsContainer}>
          <TouchableOpacity
            style={[styles.inlineTab, activeTab === 'brides' && styles.activeInlineTab]}
            onPress={() => setActiveTab('brides')}
          >
            <Image 
              source={{ uri: 'https://kalyanamitra.in/assets/ps-bride-UWGtbgl1.png' }}
              style={[styles.tabIcon, activeTab !== 'brides' && styles.inactiveTabIcon]} 
            />
            <Text style={[styles.tabText, activeTab === 'brides' && styles.activeTabText]}>Bride{"\n"}వధువు</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.inlineTab, activeTab === 'grooms' && styles.activeInlineTab]}
            onPress={() => setActiveTab('grooms')}
          >
            <Image 
              source={{ uri: 'https://kalyanamitra.in/assets/ps-groom-D-L2Y5H0.png' }}
              style={[styles.tabIcon, activeTab !== 'grooms' && styles.inactiveTabIcon]} 
            />
            <Text style={[styles.tabText, activeTab === 'grooms' && styles.activeTabText]}>Groom{"\n"}వరుడు</Text>
          </TouchableOpacity>
        </View>

        {/* Action Icons on Right */}
        <View style={styles.actionIcons}>
          <TouchableOpacity style={styles.iconButton} onPress={() => setShowSearchBar(true)}>
            <SvgIcon name="search-outline" size={20} color={colors.white} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.iconButton} onPress={() => setShowFilterSortModal(true)}>
            <SvgIcon name="options-outline" size={20} color={colors.white} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.iconButton} onPress={() => navigation.push('AddProfile', { initialGender: activeTab === 'brides' ? 'Female' : 'Male' })}>
            <SvgIcon name="person-add-outline" size={20} color={colors.white} />
          </TouchableOpacity>
        </View>
      </Animated.View>

      {/* Inline Search Bar Overlay */}
      {showSearchBar && (
        <View style={styles.searchBarOverlay}>
          <View style={styles.inlineSearchContainer}>
            <SvgIcon name="search" size={20} color={colors.accent} style={styles.searchIcon} />
            <TextInput
              style={styles.inlineSearchInput}
              placeholder="Search by name or mobile number..."
              placeholderTextColor={colors.textSecondary}
              value={searchQuery}
              onChangeText={handleSearchChange}
              autoFocus
            />
            <TouchableOpacity onPress={handleCloseSearch} style={styles.closeSearchButton}>
              <SvgIcon name="close-circle" size={24} color={colors.white} />
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Filter and Sort Modal */}
      {showFilterSortModal && (
        <View style={styles.searchModal}>
          <View style={styles.filterSortModalContent}>
            <View style={styles.searchModalHeader}>
              <Text style={styles.searchModalTitle}>Filter & Sort</Text>
              <TouchableOpacity onPress={() => setShowFilterSortModal(false)}>
                <SvgIcon name="close" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>

            {/* Filter Section */}
            <View style={styles.modalSection}>
              <Text style={styles.sectionTitle}>
                <SvgIcon name="funnel-outline" size={18} color={colors.accent} /> Filters
              </Text>
              <TouchableOpacity style={styles.optionButton} onPress={() => Alert.alert('Filter', 'Age range filter')}>
                <Text style={styles.optionText}>Age Range</Text>
                <SvgIcon name="chevron-forward" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.optionButton} onPress={() => Alert.alert('Filter', 'Location filter')}>
                <Text style={styles.optionText}>Location</Text>
                <SvgIcon name="chevron-forward" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.optionButton} onPress={() => Alert.alert('Filter', 'Education filter')}>
                <Text style={styles.optionText}>Education</Text>
                <SvgIcon name="chevron-forward" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.optionButton} onPress={() => Alert.alert('Filter', 'Profession filter')}>
                <Text style={styles.optionText}>Profession</Text>
                <SvgIcon name="chevron-forward" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.optionButton} onPress={() => Alert.alert('Filter', 'Religion filter')}>
                <Text style={styles.optionText}>Religion</Text>
                <SvgIcon name="chevron-forward" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* Sort Section */}
            <View style={styles.modalSection}>
              <Text style={styles.sectionTitle}>
                <SvgIcon name="swap-vertical-outline" size={18} color={colors.accent} /> Sort By
              </Text>
              <TouchableOpacity style={styles.optionButton} onPress={() => Alert.alert('Sort', 'Sort by newest')}>
                <Text style={styles.optionText}>Newest First</Text>
                <SvgIcon name="chevron-forward" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.optionButton} onPress={() => Alert.alert('Sort', 'Sort by age')}>
                <Text style={styles.optionText}>Age</Text>
                <SvgIcon name="chevron-forward" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.optionButton} onPress={() => Alert.alert('Sort', 'Sort by location')}>
                <Text style={styles.optionText}>Location</Text>
                <SvgIcon name="chevron-forward" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <TouchableOpacity 
              style={styles.searchModalButton} 
              onPress={() => {
                setShowFilterSortModal(false);
                // Apply filters and sort here
              }}
            >
              <Text style={styles.searchModalButtonText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Profiles List */}
      <View style={styles.profileCountHeader}>
        <Text style={styles.profileCountText}>{totalRecords} Profiles</Text>
      </View>
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.accent} />
        </View>
      ) : profiles.length === 0 ? (
        <View style={styles.emptyContainer}>
          <SvgIcon name="people-outline" size={80} color={colors.textSecondary} />
          <Text style={styles.emptyText}>No profiles found</Text>
          <Text style={styles.emptySubText}>
            {activeTab === 'brides' ? 'No bride profiles available' : 'No groom profiles available'}
          </Text>
        </View>
      ) : (
        <FlatList
          data={profiles}
          renderItem={renderProfileCard}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={styles.listContainer}
          showsVerticalScrollIndicator={false}
          onScroll={handleScroll}
          scrollEventThrottle={16}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.primary,
  },
  compactHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: colors.primary,
    borderBottomWidth: 1,
    borderBottomColor: colors.secondary,
    zIndex: 10,
  },
  inlineTabsContainer: {
    flexDirection: 'row',
    borderRadius: 16,
    padding: 2,
    gap: 2,
    borderWidth: 1,
    borderColor: colors.secondary,
  },
  inlineTab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 14,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.secondary,
  },
  activeInlineTab: {
    backgroundColor: colors.accent,
    borderColor: 'transparent',
  },
  tabIcon: {
    width: 36,
    height: 36,
    resizeMode: 'contain',
    borderRadius: 18,
    overflow: 'hidden',
  },
  inactiveTabIcon: {
    opacity: 0.5,
  },
  tabText: {
    fontSize: 12,
    color: '#D1D5DB',
    fontWeight: '500',
  },
  activeTabText: {
    color: colors.white,
    fontWeight: '600',
  },
  actionIcons: {
    flexDirection: 'row',
    gap: 12,
    marginRight: 8,
  },
  iconButton: {
    padding: 4,
  },
  searchModal: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  searchBarOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 999,
    backgroundColor: colors.primary,
    borderBottomWidth: 1,
    borderBottomColor: colors.secondary,
  },
  inlineSearchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: colors.secondary,
    margin: 10,
    borderRadius: 8,
  },
  searchIcon: {
    marginRight: 10,
  },
  inlineSearchInput: {
    flex: 1,
    fontSize: 16,
    color: colors.white,
    paddingVertical: 8,
  },
  closeSearchButton: {
    padding: 4,
    marginLeft: 8,
  },
  searchModalContent: {
    backgroundColor: colors.secondary,
    borderRadius: 12,
    padding: 20,
    width: '85%',
    maxWidth: 400,
  },
  searchModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  searchModalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.white,
  },
  searchModalInput: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingHorizontal: 15,
    paddingVertical: 12,
    fontSize: 16,
    color: colors.white,
    marginBottom: 15,
  },
  searchModalButton: {
    backgroundColor: colors.accent,
    borderRadius: 8,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  searchModalButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  filterSortModalContent: {
    backgroundColor: colors.secondary,
    borderRadius: 12,
    padding: 20,
    width: '90%',
    maxWidth: 400,
    maxHeight: '80%',
  },
  modalSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  optionButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingHorizontal: 15,
    paddingVertical: 12,
    marginBottom: 8,
  },
  optionText: {
    color: colors.white,
    fontSize: 15,
  },
  profileCountHeader: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: colors.background,
  },
  profileCountText: {
    color: colors.textSecondary,
    fontSize: 14,
    fontWeight: '600',
  },
  listContainer: {
    paddingHorizontal: 20,
    paddingBottom: 10,
    paddingTop: 10,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileCard: {
    backgroundColor: '#0F3D4A',
    borderRadius: 12,
    padding: 16,
    marginBottom: 0,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  profileName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.white,
    flex: 1,
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  iconActionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(26, 90, 107, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardBody: {
    flexDirection: 'row',
    gap: 15,
  },
  imageContainer: {
    position: 'relative',
  },
  profileImage: {
    width: 120,
    height: 120,
    borderRadius: 60,
    borderWidth: 3,
    borderColor: '#00D9B5',
  },
  approvedBadge: {
    position: 'absolute',
    top: -8,
    left: -8,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary,
    borderWidth: 2,
    borderColor: '#00D9B5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pendingBadge: {
    position: 'absolute',
    top: -8,
    left: -8,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary,
    borderWidth: 2,
    borderColor: '#FFA500',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rejectedBadge: {
    position: 'absolute',
    top: -8,
    left: -8,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary,
    borderWidth: 2,
    borderColor: '#FF6B6B',
    justifyContent: 'center',
    alignItems: 'center',
  },
  premiumBadge: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    backgroundColor: '#00D9B5',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  completionBadge: {
    position: 'absolute',
    bottom: -8,
    right: -8,
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.primary,
  },
  completionBadgeText: {
    color: colors.white,
    fontSize: 11,
    fontWeight: 'bold',
  },
  premiumText: {
    color: colors.white,
    fontSize: 12,
    fontWeight: 'bold',
  },
  profileInfo: {
    flex: 1,
    justifyContent: 'space-around',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoText: {
    color: '#E0E0E0',
    fontSize: 15,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.white,
    marginTop: 20,
  },
  emptySubText: {
    fontSize: 16,
    color: colors.textSecondary,
    marginTop: 10,
  },
  profileCardWrapper: {
    position: 'relative',
    marginBottom: 12,
    zIndex: 1,
  },
  dropdownOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 998,
    backgroundColor: 'transparent',
  },
  dropdownMenu: {
    position: 'absolute',
    top: 50,
    right: 15,
    backgroundColor: colors.gray800,
    borderRadius: 8,
    width: 240,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 10,
    zIndex: 1001,
    borderWidth: 1,
    borderColor: colors.gray700,
  },
  dropdownMenuFixed: {
    position: 'absolute',
    backgroundColor: colors.gray800,
    borderRadius: 8,
    width: 260,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 15,
    zIndex: 1001,
    borderWidth: 1,
    borderColor: colors.gray700,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 12,
  },
  dropdownItemTextContainer: {
    flex: 1,
  },
  dropdownItemText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 2,
  },
  dropdownItemSubtext: {
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 16,
  },
  dropdownDivider: {
    height: 1,
    backgroundColor: colors.gray700,
    marginHorizontal: 8,
  },
});

export default ProfilesScreen;
